/**
 * Studio view - the primary workflow (spec sections 25 and 51).
 *
 *   UPLOAD AUDIO -> SCENE MODE -> SCENE -> PREVIEW -> AUDIO EFFECT
 *   -> RENDER -> EXPORT
 *
 * The target experience: upload audio, press CREATE BEX CAST, get a file.
 * Everything else is optional refinement.
 */

import { h } from '../../utils/dom.js';
import { formatBytes, formatDuration, formatClock } from '../../utils/format.js';
import { AUDIO_EFFECTS, LOOP_MODES, RESOLUTIONS, MOODS } from '../../core/config.js';
import { describeEffect } from '../../audio/effects.js';
import { describePlan } from '../../video/loop-engine.js';
import { selectRecordingFormat } from '../../video/codec.js';
import { resolveRenderer } from '../../scenes/procedural/index.js';
import { createScenePreview } from '../components/scene-preview.js';
import { card, button, note, kv, segmented, waveform, chipRow } from '../components/common.js';

export function renderStudio(ctx) {
  const { store, settings, actions, sceneStore } = ctx;
  const state = store.get();
  const { audio, selectedScene, render } = state;
  const root = h('div', { class: 'stack-lg' });

  root.append(
    h('div', {}, [
      h('p', { class: 'eyebrow' }, ['BEX CAST visual engine']),
      h('h1', {}, ['Create an episode']),
      h('p', { class: 'muted small', style: { marginTop: '6px' } }, [
        'Upload a technology commentary clip. BEX LOOP pairs it with a reusable scene loop and exports a vertical video.',
      ]),
    ]),
  );

  /* ---------------------------------------------- 1. audio */

  root.append(
    card(
      audio
        ? [
            h('div', { class: 'between' }, [
              h('div', { style: { minWidth: '0' } }, [
                h('div', { class: 'break', style: { fontWeight: '600' } }, [audio.name]),
                h('div', { class: 'tiny dim' }, [
                  `${formatDuration(audio.duration)} \u00b7 ${formatBytes(audio.size)} \u00b7 ${audio.sampleRate} Hz \u00b7 ${audio.channels}ch`,
                ]),
              ]),
              button('Replace', { kind: 'ghost', small: true, onClick: actions.triggerAudioPick }),
            ]),
            waveform(audio.peaks),
          ]
        : [
            h(
              'button',
              {
                class: 'dropzone',
                type: 'button',
                onclick: actions.triggerAudioPick,
              },
              [
                h('div', { class: 'dropzone__icon' }, ['\u266b']),
                h('div', { style: { fontWeight: '600' } }, ['Upload audio']),
                h('div', { class: 'tiny dim' }, ['MP3, WAV, M4A, AAC or OGG - decoded locally, never uploaded']),
              ],
            ),
          ],
      { title: 'Source audio', step: 1 },
    ),
  );

  /* ---------------------------------------------- 2. scene mode */

  const modeOptions = [
    { id: 'random', label: 'Random', hint: 'Any enabled scene' },
    { id: 'mood', label: 'Mood', hint: 'Filtered by mood' },
    { id: 'manual', label: 'Manual', hint: 'Pick from library' },
    { id: 'auto', label: 'Auto', hint: 'Weighted scoring' },
  ];

  const moodChips = h(
    'div',
    { class: 'segmented' },
    MOODS.map((mood) =>
      h(
        'button',
        {
          class: 'segmented__option',
          type: 'button',
          'aria-pressed': String(settings.mood === mood.id),
          onclick: () => {
            actions.setSetting({ mood: mood.id });
            const result = actions.autoSelectScene({ mood: mood.id });
            if (result.scene) actions.render();
          },
        },
        [h('span', {}, [mood.label])],
      ),
    ),
  );

  root.append(
    card(
      [
        segmented(modeOptions, settings.sceneMode, (id) => {
          actions.setSetting({ sceneMode: id });
          if (id === 'manual') {
            actions.setView('scenes');
            return;
          }
          const result = actions.autoSelectScene({ mode: id });
          if (result.scene) actions.render();
        }),
        settings.sceneMode === 'mood' || settings.sceneMode === 'auto'
          ? h('div', { class: 'stack' }, [
              h('div', { class: 'field__label', style: { marginTop: '10px' } }, ['Mood']),
              moodChips,
            ])
          : null,
        settings.sceneMode === 'manual'
          ? note('Pick a scene from the Scenes tab, then come back here.', 'info')
          : null,
      ],
      { title: 'Scene mode', step: 2 },
    ),
  );

  /* ---------------------------------------------- 3. scene + preview */

  if (selectedScene) {
    const { renderer, isFallback } = resolveRenderer(selectedScene, { allowFallback: false });
    const hasAsset = Boolean(selectedScene.assetUrl) || Boolean(renderer);
    // While an export is running, do not animate a preview: it would compete
    // with the real-time capture for the main thread and cause dropped frames.
    const isRenderingNow = render.status === 'rendering' || render.status === 'preparing';
    const preview = createScenePreview(selectedScene, {
      width: 264,
      height: 470,
      animated: !isRenderingNow,
      strength: settings.audioReactive ? settings.audioReactiveStrength / 100 : 0,
    });
    ctx.onDispose(() => preview.dispose());
    const providerLabel = selectedScene.provider ?? (selectedScene.assetType === 'procedural' ? 'Procedural' : 'Not generated');

    const previewBlock = h('div', { class: 'preview' }, [
      h('div', { class: 'preview__frame' }, [preview.element]),
      h('div', { class: 'preview__meta' }, [
        h('div', { class: 'preview__title' }, [selectedScene.name]),
        h('div', { class: 'tiny dim' }, [selectedScene.description]),
        chipRow([
          h('span', { class: 'chip chip--accent' }, [`${selectedScene.duration}s loop`]),
          h('span', { class: `chip ${selectedScene.loopQuality === 'seamless' ? 'chip--ok' : selectedScene.loopQuality === 'repeatable' ? 'chip--info' : 'chip--warn'}` }, [
            `Loop: ${selectedScene.loopQuality}`,
          ]),
          h('span', { class: 'chip' }, [providerLabel]),
          h('span', { class: 'chip' }, [`used ${selectedScene.usageCount}\u00d7`]),
        ]),
        h('div', { class: 'btn-row' }, [
          button('Re-roll scene', { kind: 'ghost', small: true, onClick: () => {
            const result = actions.autoSelectScene();
            if (result.scene) actions.render();
          } }),
          button('Open library', { kind: 'ghost', small: true, onClick: () => actions.setView('scenes') }),
          hasAsset
            ? null
            : button('Generate asset', { kind: 'primary', small: true, onClick: () => actions.setView('generate') }),
        ]),
        isFallback ? note('This scene has no asset, so a fallback visual would be used.', 'warn') : null,
        !hasAsset
          ? note(
              'This scene has no loop yet. Generate one with Google Vids (manual) or Veo (API), or pick a procedural scene.',
              'warn',
            )
          : null,
      ]),
    ]);

    root.append(card([previewBlock], { title: 'Scene', step: 3 }));
    queueMicrotask(() => preview.start());
  } else {
    root.append(
      card([note('No scene selected. Pick a mode above or open the Scenes tab.', 'warn')], { title: 'Scene', step: 3 }),
    );
  }

  /* ---------------------------------------------- 4. audio effect */

  const effectOptions = AUDIO_EFFECTS.map((effect) => ({ id: effect.id, label: effect.label }));
  const strengthRow =
    settings.audioEffect !== 'none'
      ? h('div', { class: 'field' }, [
          h('div', { class: 'between' }, [
            h('span', { class: 'field__label' }, ['Effect strength']),
            h('span', { class: 'tiny mono', id: 'effect-strength-value' }, [`${settings.effectStrength}%`]),
          ]),
          h('input', {
            type: 'range',
            min: '0',
            max: '100',
            step: '5',
            value: String(settings.effectStrength),
            'aria-label': 'Effect strength',
            oninput: (event) => {
              const value = Number(event.target.value);
              actions.setSetting({ effectStrength: value }, { silent: true });
              const label = document.getElementById('effect-strength-value');
              if (label) label.textContent = `${value}%`;
            },
            onchange: () => actions.render(),
          }),
          h('div', { class: 'field__hint' }, [describeEffect(settings.audioEffect, settings.effectStrength)]),
        ])
      : null;

  root.append(
    card(
      [
        segmented(effectOptions, settings.audioEffect, (id) => actions.setSetting({ audioEffect: id })),
        strengthRow,
        note('Speech intelligibility has priority. Effects stay subtle by design.', 'info'),
      ],
      { title: 'Audio effect', step: 4 },
    ),
  );

  /* ---------------------------------------------- 5. render */

  const plan = actions.buildCurrentPlan();
  const format = selectRecordingFormat();
  const resolution = RESOLUTIONS.find((r) => r.id === settings.resolution) ?? RESOLUTIONS[0];
  const rendering = render.status === 'rendering' || render.status === 'preparing';

  const loopOptions = LOOP_MODES.map((mode) => ({ id: mode.id, label: mode.label }));
  const resolutionOptions = RESOLUTIONS.map((r) => ({ id: r.id, label: r.width === 1080 ? '1080p' : '720p', hint: `${r.width}\u00d7${r.height}` }));

  const renderSummary = kv([
    ['Audio length', audio ? formatDuration(audio.duration) : 'no audio'],
    ['Loop plan', plan ? describePlan(plan) : 'select a scene'],
    ['Output', `${resolution.width} x ${resolution.height} @ ${settings.fps}fps`],
    ['Container', format.chosen ? format.chosen.label : 'unavailable in this browser'],
    ['Real-time cost', audio ? `about ${formatClock(audio.duration)} of recording` : '-'],
  ]);

  const progressBlock = rendering
    ? h('div', { class: 'stack', id: 'render-progress' }, [
        h('div', { class: 'between' }, [
          h('span', { class: 'small', id: 'render-phase' }, [`Rendering - ${render.phase}`]),
          h('span', { class: 'tiny mono', id: 'render-clock' }, [
            `${formatClock(render.elapsedSeconds)} / ${formatClock(render.totalSeconds)}`,
          ]),
        ]),
        h('div', { class: 'progress' }, [
          h('div', {
            class: 'progress__bar',
            id: 'render-bar',
            style: { width: `${Math.round(Math.min(1, Math.max(0, render.progress)) * 100)}%` },
          }),
        ]),
        button('Cancel render', { kind: 'danger', small: true, onClick: actions.cancelRender }),
      ])
    : null;

  root.append(
    card(
      [
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Loop mode']),
          segmented(loopOptions, settings.loopMode, (id) => actions.setSetting({ loopMode: id })),
          h('div', { class: 'field__hint' }, [
            LOOP_MODES.find((m) => m.id === settings.loopMode)?.hint ?? '',
          ]),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Resolution']),
          segmented(resolutionOptions, settings.resolution, (id) => actions.setSetting({ resolution: id })),
        ]),
        renderSummary,
        format.warning ? note(format.warning, 'warn') : null,
        plan?.warning ? note(plan.warning, 'warn') : null,
        progressBlock,
        button(
          rendering ? 'Rendering...' : 'Create BEX CAST',
          {
            kind: 'primary',
            block: true,
            large: true,
            disabled: rendering || !audio || !selectedScene,
            onClick: actions.startRender,
          },
        ),
      ],
      { title: 'Render', step: 5 },
    ),
  );

  /* ---------------------------------------------- 6. export */

  const result = render.result;
  if (result) {
    const resultVideo = h('video', {
      src: result.url,
      controls: true,
      playsInline: true,
      preload: 'metadata',
      style: { width: '100%', borderRadius: 'var(--radius-sm)', background: '#000', maxHeight: '420px' },
    });
    root.append(
      card(
        [
          h('div', { style: { maxWidth: '260px', margin: '0 auto' } }, [resultVideo]),
          kv([
            ['File', result.filename],
            ['Container', result.container.toUpperCase()],
            ['Codec', result.format?.chosen?.videoCodec ?? 'browser default'],
            ['Size', formatBytes(result.bytes)],
            ['Dimensions', `${result.width} x ${result.height}`],
            ['Duration', formatDuration(result.durationSeconds)],
          ]),
          result.container === 'webm'
            ? note(
                'This browser recorded WebM, which is what it actually produced. The file is named .webm - it is never relabelled as MP4.',
                'info',
              )
            : note('Recorded as real MP4 (H.264 + AAC).', 'ok'),
          ...(result.warnings ?? []).map((warning) => note(warning, 'warn')),
          result.timingSource === 'wall-clock'
            ? note(
                'Timing fell back to wall-clock because the audio clock stalled. Check that the exported file has audio before publishing.',
                'warn',
              )
            : null,
          h('div', { class: 'btn-row' }, [
            button('Download', { kind: 'primary', onClick: actions.exportResult }),
            typeof navigator !== 'undefined' && navigator.share
              ? button('Share', { kind: 'ghost', onClick: actions.shareResult })
              : null,
            button('Re-render', { kind: 'ghost', onClick: actions.startRender }),
          ]),
        ],
        { title: 'Export', step: 6 },
      ),
    );
  }

  return root;
}
